import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faCircleCheck,
  faGift,
  faMoon,
  faUmbrellaBeach,
  faUtensils,
} from '@fortawesome/free-solid-svg-icons'
import { useAppNavigate } from '../../context/AppContext'
import { IProduct } from '../../types/products'
import CartPageItem from './cart-page-item/CartPageItem'
import MenuItem from '../../components/menu-item/MenuItem'
import Loader from '../../components/loader/Loader'
import { PandaBubble } from '../../components/panda-sign/PandaBubble'
import ViewRouletteWheel from './ViewRouletteWheel'
import { useGetProductsQuery } from '../../store/api/productsApi'
import { useCartMinimums } from '../../utils/useCartMinimums'
import { useStoreHours } from '../../utils/useStoreHours'
import { getGeneralInfo } from '../../utils/generalInfo'
import { cartCount, cartTotal } from '../../utils/cart'
import { PandaHead, PandaSign } from '../../components/panda-sign/PandaSign'
import { confetti } from '../../utils/fx'
import { useDeviceDetect } from '../../utils/use-device-detect'
import './CartPage.scss'

const c = 'cart-page'

const CartPage = () => {
  const navigate = useAppNavigate()
  const { t } = useTranslation()
  const { cart, setCart } = useOutletContext<{
    cart: IProduct[]
    setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
  }>()
  const { checkedCart, blocked, hasMinimumErrors } = useCartMinimums(cart)
  const [openWheel, setOpenWheel] = useState(false)
  const { isOutsideHours } = useStoreHours()
  const { isDesktop } = useDeviceDetect()
  const generalInfo = getGeneralInfo()

  const { data: productsData = [], isLoading: loadingProducts } =
    useGetProductsQuery()

  const additionalProducts = useMemo(() => {
    return productsData
      .map((item: { items: IProduct[] }) =>
        item.items.filter((product: IProduct) => product.isAdditional),
      )
      .flat()
  }, [productsData])

  const totalPrice = cartTotal(cart)
  const minPrice = Number(generalInfo?.minPrice) || 0
  const rouletteMin = Number(generalInfo?.rouletteMinPrice) || 0
  const showRoulette = !!(rouletteMin && generalInfo.isRouletteActive)
  const progressPercentage = Math.min(
    (totalPrice / (rouletteMin || 1)) * 100,
    100,
  )
  const previousProgress = useRef(progressPercentage)

  useEffect(() => {
    if (showRoulette && previousProgress.current < 100 && progressPercentage >= 100) {
      confetti()
    }
    previousProgress.current = progressPercentage
  }, [progressPercentage, showRoulette])

  useEffect(() => {
    scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  function disableCart() {
    if (generalInfo.dayOff) {
      return true
    }

    if (isOutsideHours) {
      return true
    }

    if (!cart.length) {
      return true
    }

    if (totalPrice < minPrice) {
      return true
    }

    return false
  }

  if (!cart.length) {
    return (
      <div className={`${c} ${c}--empty`}>
        <div className={`${c}__empty`}>
          <PandaSign size={100} className={`${c}__empty-panda`}>
            <FontAwesomeIcon icon={faUtensils} /> {t('SIGN_HUNGRY')}
          </PandaSign>
          <h2>{t('CART_IS_EMPTY')}</h2>
          <p>{t('CART_EMPTY_HINT')}</p>
          <button onClick={() => navigate('/')}>
            {t('GO_TO_MENU')} <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
      </div>
    )
  }

  const checkoutDisabled = disableCart() || blocked

  return (
    <div className={c}>
      {isDesktop && (
        <h1 className={`${c}__title`}>
          {t('CART')}
          <span>{cartCount(cart)}</span>
        </h1>
      )}

      <div className={`${c}__items`}>
        {checkedCart.map((item: IProduct, index) => (
          <CartPageItem data={item} key={item._id} index={index} setCart={setCart} />
        ))}
      </div>

      {showRoulette && (
        <div className={`${c}__roulette${progressPercentage >= 100 ? ' is-unlocked' : ''}`}>
          <div className={`${c}__roulette-head`}>
            <FontAwesomeIcon icon={faGift} />
            <span>
              {progressPercentage >= 100 ? (
                t('ROULETTE_UNLOCKED')
              ) : (
                <Trans
                  i18nKey='ROULETTE_LEFT'
                  values={{ amount: Math.max(0, rouletteMin - totalPrice) }}
                />
              )}
            </span>
          </div>
          <div className={`${c}__roulette-row`}>
            <div className={`${c}__roulette-track`}>
              <div
                className={`${c}__roulette-fill`}
                style={{ width: `${progressPercentage}%` }}
              />
              <span
                className={`${c}__roulette-runner`}
                style={{ left: `clamp(17px, ${progressPercentage}%, calc(100% - 17px))` }}
              >
                <PandaHead />
              </span>
            </div>
            <span className={`${c}__roulette-goal`}>
              <FontAwesomeIcon icon={faGift} />
            </span>
          </div>
          <div className={`${c}__roulette-foot`}>
            {progressPercentage >= 100 ? (
              <span className={`${c}__roulette-done`}>
                <FontAwesomeIcon icon={faCircleCheck} /> {rouletteMin} lei
              </span>
            ) : (
              <span>
                <b>{totalPrice}</b> / {rouletteMin} lei · {t('PROGRESS_TO_ROULETTE')}
              </span>
            )}
            <button onClick={() => setOpenWheel(true)}>{t('VIEW_ROULETTE_PRIZES')}</button>
          </div>
        </div>
      )}

      {loadingProducts ? (
        <Loader size={40} />
      ) : additionalProducts.length ? (
        <div className={`${c}__extras`}>
          <h2>{t('SOMETHING_ELSE')}</h2>
          <div className={`${c}__extras-track`}>
            {additionalProducts.map((product: IProduct, index: number) => (
              <MenuItem
                key={product._id}
                index={index}
                data={product}
                cart={cart}
                setCart={setCart}
                compact
              />
            ))}
          </div>
        </div>
      ) : null}

      {generalInfo.cartPromoMessage && (
        <PandaBubble message={generalInfo.cartPromoMessage} />
      )}

      <div className={`${c}__summary`}>
        <div className={`${c}__summary-row`}>
          <span>{t('ORDER_SUM')}</span>
          <strong key={totalPrice}>{totalPrice} lei</strong>
        </div>
        {hasMinimumErrors && (
          <div className='minimum-quantity minimum-quantity--error' role='alert'>
            {t('CART_MINIMUM_ERROR')}
          </div>
        )}
        {totalPrice < minPrice && (
          <div className={`${c}__note`}>
            <Trans i18nKey='MINIMUM_PRICE' values={{ minPrice: generalInfo?.minPrice }} />
          </div>
        )}
        {!generalInfo?.dayOff && isOutsideHours && (
          <div className={`${c}__alert`}>
            <FontAwesomeIcon icon={faMoon} />{' '}
            <Trans
              i18nKey='TIME_LIMIT'
              values={{ minTime: generalInfo?.minTime, maxTime: generalInfo.maxTime }}
            />
          </div>
        )}
        {generalInfo?.dayOff && (
          <div className={`${c}__alert`}>
            <FontAwesomeIcon icon={faUmbrellaBeach} /> <Trans i18nKey='DAY_OFF' />
          </div>
        )}
        {isDesktop && (
          <button
            className={`${c}__checkout`}
            disabled={checkoutDisabled}
            onClick={() => navigate('checkout')}
          >
            {t('FINALIZE_ORDER')} <FontAwesomeIcon icon={faArrowRight} />
          </button>
        )}
      </div>

      {!isDesktop && (
        <div className={`${c}__bar`}>
          <div className={`${c}__bar-total`}>
            <small>{t('TOTAL')}</small>
            <strong key={totalPrice}>{totalPrice} lei</strong>
          </div>
          <button
            className={`${c}__checkout`}
            disabled={checkoutDisabled}
            onClick={() => navigate('checkout')}
          >
            {t('FINALIZE_ORDER')} <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
      )}

      {openWheel && <ViewRouletteWheel setOpen={setOpenWheel} />}
    </div>
  )
}

export default CartPage
